/**
 * PostgREST member-self read/CRUD accessors for the member app (plan 22 — runs
 * 13/14). All rows are RLS-scoped Pattern B (member owns rows). These are query
 * builders returning the Supabase filter builder so callers may await or chain.
 *
 * Covers: own check-in history (stats MB-03), occupancy density heatmap (MA-06),
 * own membership freezes (MA-08 read), and personal_workouts CRUD (MB-01).
 */
import type { SupabaseClient } from '@supabase/supabase-js';

import type { GymSupabaseClient } from '../supabase.js';

/** Untyped escape hatch for RPCs not in the generated Database['Functions'] map. */
function untyped(client: GymSupabaseClient): SupabaseClient {
  return client as unknown as SupabaseClient;
}

/** Check-in results that count as a successful gym visit for stats. */
export const VISIT_RESULTS = ['granted', 'granted_override', 'allowed_warn_overdue'] as const;

export interface CheckinHistoryQuery {
  memberId: string;
  /** ISO timestamptz lower bound (default: 90 days before now). */
  since?: string;
}

/** Days of history to read for the stats screen by default. */
export const STATS_HISTORY_DAYS = 90;

/** Own successful check-ins, oldest→newest, for client-side stat derivation. */
export function getMyCheckinHistory(client: GymSupabaseClient, query: CheckinHistoryQuery) {
  const since = query.since ?? new Date(Date.now() - STATS_HISTORY_DAYS * 86_400_000).toISOString();
  return client
    .from('checkins')
    .select('checked_in_at,result')
    .eq('member_id', query.memberId)
    .in('result', [...VISIT_RESULTS])
    .gte('checked_in_at', since)
    .order('checked_in_at', { ascending: true });
}

export interface DensityQuery {
  tenantId: string;
  locationId: string;
  granularity?: 'hourly' | 'daily' | 'monthly';
  /** ISO bounds; default last 30 days for the hourly heatmap. */
  from?: string;
  to?: string;
}

/**
 * Historical occupancy density for the heatmap (MA-06).
 *
 * IMPORTANT: the `occupancy_snapshots` table is NEVER written by anything in the
 * repo (no trigger/cron/Edge Fn/migration inserts into it), so reading it would
 * always return zero rows and the heatmap would be permanently empty. The live
 * density source is the get_density(...) SECURITY DEFINER RPC (0051), which
 * aggregates granted check-ins on the fly into hourly/daily/monthly buckets,
 * tenant-pinned by p_tenant_id and RLS-safe. We call that instead.
 *
 * Returns a `{ data, error }`-shaped thenable (same shape PostgREST builders
 * resolve to) so callers can `await ...then(({ data }) => ...)` unchanged.
 * RPC names are not in the generated Database['Functions'] map, so we go through
 * the untyped client view (same pattern as listCommissionSummary).
 */
export function getOccupancyDensity(client: GymSupabaseClient, query: DensityQuery) {
  const granularity = query.granularity ?? 'hourly';
  const from = query.from ?? new Date(Date.now() - 30 * 86_400_000).toISOString();
  const to = query.to ?? new Date().toISOString();
  return untyped(client).rpc('get_density', {
    p_tenant_id: query.tenantId,
    p_location_id: query.locationId,
    p_granularity: granularity,
    p_from: from,
    p_to: to,
  });
}

/**
 * Resolve the start time of an arbitrary set of class sessions BY ID (not by a
 * calendar window). The personal calendar (MA-07) needs the session start time
 * for every booked session — including ones outside any fixed now..now+Nd
 * window — so it can date each booking on its real session day instead of the
 * booking-creation day. RLS scopes class_sessions to the caller's tenant.
 * Returns `{ data: [{ id, starts_at }], error }`.
 */
export function getSessionStartTimes(client: GymSupabaseClient, sessionIds: string[]) {
  return client.from('class_sessions').select('id,starts_at').in('id', sessionIds);
}

/** Own membership freezes (read; write goes via functions/freeze.requestFreeze). */
export function getMyFreezes(client: GymSupabaseClient, memberId: string) {
  return client
    .from('membership_freezes')
    .select('*')
    .eq('member_id', memberId)
    .order('requested_at', { ascending: false });
}

// ---------------------------------------------------------------------------
// personal_workouts CRUD (MB-01) — member-owned (Pattern B)
// ---------------------------------------------------------------------------

export interface CreatePersonalWorkoutInput {
  scheduled_at: string; // ISO timestamptz, future
  duration_minutes?: number;
  title?: string;
  notes?: string;
}

// `personal_workouts` is introduced by migration 0120_member_stats (run 14)
// and is not yet in the generated @gym/db-types union, so PostgREST table typing
// resolves to `never`. Until db-types is regenerated centrally we go through a
// minimally-typed view of the client for this one table; the query shapes are
// still asserted by the run14 DB test.
type UntypedFrom = {
  from: (table: string) => {
    select: (cols: string) => {
      eq: (
        c: string,
        v: string,
      ) => {
        gte: (c: string, v: string) => { order: (c: string, o: { ascending: boolean }) => unknown };
      };
    };
    insert: (row: Record<string, unknown>) => {
      select: (cols: string) => { single: () => unknown };
    };
    delete: () => { eq: (c: string, v: string) => unknown };
  };
};

function pw(client: GymSupabaseClient): UntypedFrom {
  return client as unknown as UntypedFrom;
}

/** Upcoming personal workouts for a member, soonest first. */
export function listPersonalWorkouts(
  client: GymSupabaseClient,
  memberId: string,
  fromIso: string = new Date(Date.now() - 86_400_000).toISOString(),
) {
  return pw(client)
    .from('personal_workouts')
    .select('*')
    .eq('member_id', memberId)
    .gte('scheduled_at', fromIso)
    .order('scheduled_at', { ascending: true });
}

/**
 * Insert a personal workout. tenant_id/member_id are filled by the caller from
 * the verified claims (RLS WITH CHECK requires member_id = auth.member_id()).
 */
export function createPersonalWorkout(
  client: GymSupabaseClient,
  tenantId: string,
  memberId: string,
  input: CreatePersonalWorkoutInput,
) {
  return pw(client)
    .from('personal_workouts')
    .insert({
      tenant_id: tenantId,
      member_id: memberId,
      scheduled_at: input.scheduled_at,
      duration_minutes: input.duration_minutes ?? null,
      title: input.title ?? null,
      notes: input.notes ?? null,
    })
    .select('*')
    .single();
}

/** Delete a personal workout by id (RLS scopes to the owner). */
export function deletePersonalWorkout(client: GymSupabaseClient, id: string) {
  return pw(client).from('personal_workouts').delete().eq('id', id);
}
